<template>
	<div class="input-color-picker">
		<photoshop-picker v-model="localColor" :preset-colors="presetColors" />
	</div>
</template>

<script>
import { Sketch } from 'vue-color';

const defaultColor = '#194d33';
const presetColors = ['#FFFFFF', '#191A19', '#7D7D7D', '#E72766', '#2894F0', '#512BA5', '#E9BE2E'];

export default {
	name: 'InputColorPicker',
	components: {
		'photoshop-picker': Sketch,
	},
	props: {
		color: { type: String },
		enableTransparent: { type: Boolean, default: false },
	},
	computed: {
		localColor: {
			set(value) {
				this.$emit('update:color', value.hex8);
			},
			get() {
				return this.color || defaultColor;
			},
		},
		presetColors() {
			return this.enableTransparent ? ['transparent', ...presetColors] : [...presetColors, '#FD5B31'];
		},
	},
	data() {
		return {};
	},
};
</script>

<style scoped lang="scss">
.input-color-picker ::v-deep {
	// Overrides:
	.vc-sketch {
		display: flex;
		flex-direction: column;
		padding: 0;
		background-color: var(--bg-color) !important;
		box-shadow: none !important;

		.vc-sketch-presets {
			order: -1;
			border: 0;
		}
		.vc-sketch-field {
			.vc-input__label,
			.vc-sketch-field--single {
				display: none;
			}
			.vc-input__input {
				font-family: monospace;
				width: 110px;
				margin: 0 45px;
				padding: 4px 0;
				text-align: center;
				border-color: #dcdde0;
			}

			.vc-editable-input {
				position: relative;

				&:before {
					content: '#';
					position: absolute;
					top: 6px;
					left: 73px;
					font-family: monospace;
					font-size: 11px;
				}
			}
		}
		.vc-sketch-controls {
			flex-direction: row-reverse;

			.vc-sketch-sliders {
				display: flex;
				flex-direction: column;
				justify-content: space-around;
				margin-right: 8px;

				> div {
					height: 6px;
				}
			}
			.vc-sketch-color-wrap {
				overflow: hidden;
				border-radius: 50px;
				margin: 10px;
			}
		}
	}
	.vc-checkerboard {
		background-size: cover !important;
	}
	.vc-alpha-checkboard-wrap {
		border-radius: 10px;
	}
	.vc-hue-picker,
	.vc-alpha-picker {
		height: 12px;
		width: 12px;
		margin-top: -3px;
		border-radius: 20px;
	}
	.vc-sketch-alpha-wrap {
		overflow: visible !important;
	}
}

// As coppied from vue-color - no changes
::v-deep {
	// Sketch css
	.vc-sketch {
		position: relative;
		width: 200px;
		padding: 10px 10px 0;
		box-sizing: initial;
		background: var(--bg-color);
		border-radius: 4px;
		box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.15);
	}
	.vc-sketch-saturation-wrap {
		width: 100%;
		padding-bottom: 75%;
		position: relative;
		overflow: hidden;
	}
	.vc-sketch-controls {
		display: flex;
	}
	.vc-sketch-sliders {
		padding: 4px 0;
		flex: 1;
	}
	.vc-sketch-sliders .vc-hue,
	.vc-sketch-sliders .vc-alpha-gradient {
		border-radius: 2px;
	}
	.vc-sketch-hue-wrap {
		position: relative;
		height: 10px;
	}
	.vc-sketch-alpha-wrap {
		position: relative;
		height: 10px;
		margin-top: 4px;
		overflow: hidden;
	}
	.vc-sketch-color-wrap {
		width: 24px;
		height: 24px;
		position: relative;
		margin-top: 4px;
		margin-left: 4px;
		border-radius: 3px;
	}
	.vc-sketch-active-color {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		border-radius: 2px;
		box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.15), inset 0 0 4px rgba(0, 0, 0, 0.25);
		z-index: 2;
	}
	.vc-sketch-color-wrap .vc-checkerboard {
		background-size: auto;
	}
	.vc-sketch-field {
		display: flex;
		padding-top: 4px;
	}
	.vc-sketch-field .vc-input__input {
		width: 90%;
		padding: 4px 0 3px 10%;
		border: none;
		box-shadow: inset 0 0 0 1px #ccc;
		font-size: 10px;
	}
	.vc-sketch-field .vc-input__label {
		display: block;
		text-align: center;
		font-size: 11px;
		color: #222;
		padding-top: 3px;
		padding-bottom: 4px;
		text-transform: capitalize;
	}
	.vc-sketch-field--single {
		flex: 1;
		padding-left: 6px;
	}
	.vc-sketch-field--double {
		flex: 2;
	}
	.vc-sketch-presets {
		margin-right: -10px;
		margin-left: -10px;
		padding-left: 10px;
		padding-top: 10px;
		border-top: 1px solid #eee;
	}
	.vc-sketch-presets-color {
		border-radius: 3px;
		overflow: hidden;
		position: relative;
		display: inline-block;
		margin: 0 10px 10px 0;
		vertical-align: top;
		cursor: pointer;
		width: 16px;
		height: 16px;
		box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.15);

		.vc-checkerboard {
			&::before {
				content: '';
				position: absolute;
				top: 6px;
				left: -10px;
				font-size: 20px;
				border-bottom: 2px red solid;
				width: 34px;
				transform: rotate(50deg);
			}
		}
	}
	.vc-sketch-presets-color .vc-checkerboard {
		box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.15);
		border-radius: 3px;
	}
	.vc-sketch__disable-alpha .vc-sketch-color-wrap {
		height: 10px;
	}

	// vue-color common styles - all taken different stylesheets injected by vue-color

	// vc-saturation:
	.vc-saturation,
	.vc-saturation--white,
	.vc-saturation--black {
		cursor: pointer;
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
	}
	.vc-saturation--white {
		background: linear-gradient(to right, #fff, rgba(255, 255, 255, 0));
	}
	.vc-saturation--black {
		background: linear-gradient(to top, #000, rgba(0, 0, 0, 0));
	}
	.vc-saturation-pointer {
		cursor: pointer;
		position: absolute;
	}
	.vc-saturation-circle {
		cursor: head;
		width: 4px;
		height: 4px;
		box-shadow: 0 0 0 1.5px #fff, inset 0 0 1px 1px rgba(0, 0, 0, 0.3), 0 0 1px 2px rgba(0, 0, 0, 0.4);
		border-radius: 50%;
		transform: translate(-2px, -2px);
	}

	// vc-hue:
	.vc-hue {
		position: absolute;
		top: 0px;
		right: 0px;
		bottom: 0px;
		left: 0px;
		border-radius: 2px;
	}
	.vc-hue--horizontal {
		background: linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%);
	}
	.vc-hue--vertical {
		background: linear-gradient(to top, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%);
	}
	.vc-hue-container {
		cursor: pointer;
		margin: 0 2px;
		position: relative;
		height: 100%;
	}
	.vc-hue-pointer {
		z-index: 2;
		position: absolute;
	}
	.vc-hue-picker {
		cursor: pointer;
		margin-top: 1px;
		width: 4px;
		border-radius: 1px;
		height: 8px;
		box-shadow: 0 0 2px rgba(0, 0, 0, 0.6);
		background: #fff;
		transform: translateX(-2px);
	}

	// vc-alpha
	.vc-alpha {
		position: absolute;
		top: 0px;
		right: 0px;
		bottom: 0px;
		left: 0px;
	}
	.vc-alpha-checkboard-wrap {
		position: absolute;
		top: 0px;
		right: 0px;
		bottom: 0px;
		left: 0px;
		overflow: hidden;
	}
	.vc-alpha-gradient {
		position: absolute;
		top: 0px;
		right: 0px;
		bottom: 0px;
		left: 0px;
	}
	.vc-alpha-container {
		cursor: pointer;
		position: relative;
		z-index: 2;
		height: 100%;
		margin: 0 3px;
	}
	.vc-alpha-pointer {
		z-index: 2;
		position: absolute;
	}
	.vc-alpha-picker {
		cursor: pointer;
		width: 4px;
		border-radius: 1px;
		height: 8px;
		box-shadow: 0 0 2px rgba(0, 0, 0, 0.6);
		background: #fff;
		margin-top: 1px;
		transform: translateX(-2px);
	}

	// vc-checkboard

	.vc-checkerboard {
		position: absolute;
		top: 0px;
		right: 0px;
		bottom: 0px;
		left: 0px;
		background-size: contain;
	}

	// vc-editable-input:

	.vc-editable-input {
		position: relative;
	}
	.vc-input__input {
		padding: 0;
		border: 0;
		outline: none;
	}
	.vc-input__label {
		text-transform: capitalize;
	}
}
</style>
